import { useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { CaseId } from '@casetrace/shared';
import { createPstUpload, startImport, uploadSourceFile } from '../../lib/callables';
import { Button } from '../../components/ui/button';
import { SourceTypeIcon } from './source-type-icon';

const PST_CONTENT_TYPE = 'application/vnd.ms-outlook';

export function PstUploadButton({ caseId }: { readonly caseId: CaseId }) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!file.name.toLowerCase().endsWith('.pst')) {
        throw new Error('Kun PST-filer støttes for Outlook-import.');
      }
      const upload = await createPstUpload({ caseId, navn: 'Outlook PST', fileName: file.name });
      await uploadSourceFile({
        caseId,
        sourceId: upload.sourceId,
        uploadObjectPath: upload.uploadObjectPath,
        file,
        contentType: PST_CONTENT_TYPE,
      });
      return startImport({ caseId, sourceId: upload.sourceId });
    },
  });

  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    uploadMutation.mutate(file);
    event.currentTarget.value = '';
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pst,application/vnd.ms-outlook"
        className="hidden"
        onChange={handleFileSelected}
      />
      <Button
        className="mt-2"
        variant="outline"
        disabled={uploadMutation.isPending}
        onClick={() => fileInputRef.current?.click()}
      >
        <SourceTypeIcon type="pst" />
        Last opp Outlook PST
      </Button>
      {uploadMutation.isPending && (
        <p className="mt-2 text-xs text-text-tertiary">Laster opp PST og starter import...</p>
      )}
      {uploadMutation.error && (
        <p className="mt-2 text-xs text-danger/80">Kunne ikke importere Outlook PST. Prøv igjen.</p>
      )}
    </>
  );
}
